import Modal from "../../components/modal";
import DatafieldSection from "../../components/datafield-section";
import {
  UnderwritingPolicyAttachment,
  ApplicabilityStrategy,
} from "../../models/underwritingPolicyAttachment";
import { dateToDDMMYYYY } from "../../utils";
import { getDisplayStatus } from "./underwritingUtils";
import { getBboxFinderUrl, getGoogleMapsPointUrl, renderSegmentIdLink } from "./underwritingUtils";
import UnderwritingUiCompareLink from "../underwriting-ui/UnderwritingUiCompareLink";
import {
  computeActorInfo,
  getActorRoleLabel,
  getEventActor,
} from "./actorUtils";

interface PolicyAttachmentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  attachment: UnderwritingPolicyAttachment | null;
}

export default function PolicyAttachmentDetailsModal({
  isOpen,
  onClose,
  attachment,
}: PolicyAttachmentDetailsModalProps) {
  if (!attachment) {
    return null;
  }

  const basicInfoLabels = [
    {
      items: [
        { key: "_id", label: "Attachment ID:" },
        { key: "employerId", label: "Employer ID:" },
        { key: "status", label: "Status:" },
        { key: "description", label: "Description:" },
        {
          key: "policies",
          label: "Policies:",
          formatMethod: (policies: string[]) =>
            policies?.join(", ") || "-",
        },
        { key: "experimentName", label: "Experiment Name:" },
        { key: "priority", label: "Priority:" },
        { key: "createdBy", label: "Created By:" },
        { key: "approvedBy", label: "Approved By:" },
        {
          key: "createdAt",
          label: "Created At:",
          formatMethod: (date: string) => (date ? dateToDDMMYYYY(date) : "-"),
        },
        {
          key: "updatedAt",
          label: "Updated At:",
          formatMethod: (date: string) => (date ? dateToDDMMYYYY(date) : "-"),
        },
      ],
    },
  ];

  const geoConfig = attachment.applicabilityMetadata?.geoConfig;
  const googleMapsPointUrl = getGoogleMapsPointUrl(geoConfig?.circle);
  const bboxFinderUrl = getBboxFinderUrl(geoConfig?.boundingBox);
  const actorInfo = computeActorInfo(attachment.statusLogs || []);
  const derivedData = {
    ...attachment,
    createdBy: actorInfo.createdBy || attachment.createdBy,
    approvedBy: actorInfo.approvedBy,
  };

  const applicabilityLabels = attachment.applicabilityMetadata
    ? [
        {
          title: "Applicability Metadata",
          items: [
            { key: "applicabilityMetadata.strategy", label: "Strategy:" },
            ...(attachment.applicabilityMetadata.strategy ===
            ApplicabilityStrategy.GEOLOCATION
              ? [
                  {
                    key: "applicabilityMetadata.geoConfig.maxStaleDurationHours",
                    label: "Match Window (hours):",
                  },
                ]
              : attachment.applicabilityMetadata.strategy ===
            ApplicabilityStrategy.SEGMENT
              ? [
                  {
                    key: "applicabilityMetadata.segmentId",
                    label: "Segment ID:",
                    formatMethod: (segmentId: string) =>
                      renderSegmentIdLink(segmentId),
                  },
                ]
              : [
                  {
                    key: "applicabilityMetadata.userIdSuffixes",
                    label: "User ID Suffixes:",
                    formatMethod: (suffixes: string[]) =>
                      suffixes?.join(", ") || "-",
                  },
                ]),
          ],
        },
      ]
    : [];

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Policy Attachment Details"
      hasSubmitButton={false}
      hasCancelButton={true}
      cancelButtonText="Close"
      size="large"
    >
      <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <div className="mb-4">
          <DatafieldSection labels={basicInfoLabels} data={derivedData} />
        </div>

        {applicabilityLabels.length > 0 && (
          <div className="mb-4">
            <DatafieldSection labels={applicabilityLabels} data={attachment} />
            {geoConfig?.circle && (
              <div className="mt-2">
                <div><strong>Circle:</strong> lat {geoConfig.circle.lat}, lng {geoConfig.circle.lng}, radius {geoConfig.circle.radiusMetres}m</div>
                <a href={googleMapsPointUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary mt-2">
                  View point in Google Maps ↗
                </a>
              </div>
            )}
            {geoConfig?.boundingBox && (
              <div className="mt-2">
                <div>
                  <strong>Bounding Box:</strong> minLat {geoConfig.boundingBox.minLat}, maxLat {geoConfig.boundingBox.maxLat}, minLng {geoConfig.boundingBox.minLng}, maxLng {geoConfig.boundingBox.maxLng}
                </div>
                <a href={bboxFinderUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary mt-2">
                  View bounding box in bboxfinder ↗
                </a>
              </div>
            )}
          </div>
        )}

        {attachment.policyDiffMappings && attachment.policyDiffMappings.some(m => m.oldPolicy) && (
          <div className="mb-4">
            <h6>Policy Diffs</h6>
            <table className="table table-sm table-bordered" style={{ tableLayout: "fixed", width: "100%" }}>
              <colgroup>
                <col style={{ width: "25%" }} />
                <col style={{ width: "25%" }} />
                <col style={{ width: "40%" }} />
                <col style={{ width: "10%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Old Policy</th>
                  <th>New Policy</th>
                  <th>Summary</th>
                  <th>Diff</th>
                </tr>
              </thead>
              <tbody>
                {attachment.policyDiffMappings.filter(m => m.oldPolicy).map((mapping) => (
                  <tr key={mapping.newPolicy}>
                    <td style={{ wordBreak: "break-all" }}><small>{mapping.oldPolicy}</small></td>
                    <td style={{ wordBreak: "break-all" }}><small>{mapping.newPolicy}</small></td>
                    <td style={{ overflow: "hidden", wordBreak: "break-all" }}>
                      {mapping.diff ? (
                        <div style={{ fontSize: '0.8rem' }}>
                          <div className="mb-1">
                            <span className="text-success">+{mapping.diff.summary.total_added} added</span>
                            {' · '}
                            <span className="text-danger">-{mapping.diff.summary.total_removed} removed</span>
                            {' · '}
                            <span className="text-warning">~{mapping.diff.summary.total_modified} modified</span>
                          </div>
                          {mapping.diff.summary.sections_changed.length > 0 && (
                            <div className="text-muted mb-1">
                              Sections: {mapping.diff.summary.sections_changed.join(', ')}
                            </div>
                          )}
                          {mapping.diff.business_summary.effect && (
                            <span className={`badge mb-1 ${
                              mapping.diff.business_summary.effect === 'mixed' ? 'bg-warning text-dark' :
                              mapping.diff.business_summary.effect === 'more_restrictive' ? 'bg-danger' :
                              mapping.diff.business_summary.effect === 'identical' ? 'bg-secondary' :
                              'bg-success'
                            }`}>
                              {mapping.diff.business_summary.effect}
                            </span>
                          )}
                          {mapping.diff.business_summary.key_changes.length > 0 && (
                            <ul className="mb-0 ps-3">
                              {mapping.diff.business_summary.key_changes.map((change, i) => (
                                <li key={i}>{change}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : <small className="text-muted">—</small>}
                    </td>
                    <td>
                      <UnderwritingUiCompareLink
                        basePolicy={mapping.oldPolicy}
                        targetPolicy={mapping.newPolicy}
                      >
                        View ↗
                      </UnderwritingUiCompareLink>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {attachment.statusLogs && attachment.statusLogs.length > 0 && (
          <div className="mb-4">
            <h6>Status Log</h6>
            <table className="table table-sm table-bordered">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Role</th>
                  <th>Actor</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {[...attachment.statusLogs]
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((log, i) => (
                    <tr key={i}>
                      <td>{getDisplayStatus(log.status)}</td>
                      <td>{getActorRoleLabel(log.status)}</td>
                      <td>{getEventActor(log)}</td>
                      <td>{dateToDDMMYYYY(log.createdAt)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
